import { logger } from "@/lib/logger";
/**
 * PHC Sync Service - Background sync from IndexedDB to Supabase
 * Processes unsynced consultations and referrals when connectivity is available.
 */

import { supabase } from '@/integrations/supabase/client';
import { phcOfflineDb } from './phcOfflineDb';
import { safeIsOnline } from '@/lib/browserSafeUtils';
import { syncVhfSignals } from './vhfSignalSync';

let isSyncing = false;

export async function syncPHCData(): Promise<{ synced: number; failed: number }> {
  if (isSyncing || !safeIsOnline()) return { synced: 0, failed: 0 };
  isSyncing = true;
  let synced = 0;
  let failed = 0;

  try {
    // Sync consultations
    const unsynced = await phcOfflineDb.getUnsyncedConsultations();
    for (const c of unsynced) {
      try {
        const { data, error } = await supabase
          .from('phc_consultations')
          .insert({
            patient_id: c.patientId,
            chw_user_id: c.chwUserId,
            chief_complaint: c.chiefComplaint,
            // danger_signs has no column on phc_consultations.
            // Fold any captured danger signs into the symptoms array so they
            // are preserved without breaking the insert.
            symptoms: [...(c.symptoms || []), ...(c.dangerSigns || []).map(d => `[DANGER] ${d}`)],
            triage_level: c.triageLevel,
            ai_cdst_used: c.aiCdstUsed,
            ai_cdst_result: c.aiCdstResult,
            cdst_chw_override: c.cdstChwOverride,
            cdst_override_reason: c.cdstOverrideReason,
            cdst_final_triage: c.cdstFinalTriage,
            cdst_time_to_decision_seconds: c.cdstTimeToDecisionSeconds,
            referral_needed: c.referralNeeded,
            referral_facility: c.referralFacility,
            treatment_given: c.treatmentGiven,
            consultation_outcome: c.consultationOutcome,
            follow_up_date: c.followUpDate,
            follow_up_notes: c.followUpNotes,
            notes: c.notes,
            location_lat: c.locationLat,
            location_lng: c.locationLng,
            encounter_type: c.encounterType ?? null,
            comorbidities: c.comorbidities ?? [],
            vulnerable_flags: c.vulnerableFlags ?? [],
            is_vulnerable: c.isVulnerable ?? false,
            household_ref: c.householdRef ?? null,
            synced_at: new Date().toISOString(),
          })
          .select('id')
          .single();

        if (error) throw error;
        await phcOfflineDb.markSynced(c.localId, data.id);
        synced++;
      } catch (err) {
        logger.error('[PHC Sync] Failed to sync consultation:', err);
        failed++;
      }
    }

    // Sync patient assignments to linked trial sites
    try {
      const { data: links } = await (supabase as any)
        .from('phc_site_links_safe')
        .select('id, trial_site_id')
        .eq('sync_enabled', true)
        .eq('auto_assign_patients', true);

      if (links && links.length > 0) {
        // Get all unique patient profile IDs from the consultations we just synced
        // (NOT re-fetching from IndexedDB — those are already marked synced)
        const profileIds = [...new Set(unsynced.map(c => c.patientId))];

        // PTSA requires patient_id=profiles.user_id AND profile_id=profiles.id
        // Resolve profile.id → profile.user_id for the legacy FK
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, user_id')
          .in('id', profileIds);

        const profileMap = new Map((profiles || []).map(p => [p.id, p.user_id]));

        for (const link of links) {
          for (const profileId of profileIds) {
            const authUserId = profileMap.get(profileId);
            if (!authUserId) {
              logger.warn('[PHC Sync] No auth user_id found for profile:', profileId);
              continue;
            }
            try {
              // patient_id → profiles.user_id (legacy FK), profile_id → profiles.id
              const { error: assignErr } = await supabase
                .from('patient_trial_site_assignments')
                .insert({
                  patient_id: authUserId,
                  profile_id: profileId,
                  trial_site_id: link.trial_site_id,
                  assignment_source: 'phc_auto_sync',
                  status: 'registered',
                } as any);
              // Ignore duplicate errors
              if (assignErr && !assignErr.message?.includes('duplicate')) {
                logger.warn('[PHC Sync] Assignment insert warning:', assignErr.message);
              }
            } catch {
              // Non-critical - the DB trigger should handle this
            }
          }
        }
      }
    } catch (err) {
      logger.error('[PHC Sync] Site assignment sync error:', err);
    }
    // Sync referrals
    const unsyncedRefs = await phcOfflineDb.getUnsyncedReferrals();
    for (const r of unsyncedRefs) {
      try {
        // Find the remote consultation ID
        const consultations = await phcOfflineDb.getPatientConsultations(r.patientId);
        const linked = consultations.find(c => c.localId === r.consultationLocalId);
        if (!linked?.remoteId) {
          // Consultation not synced yet, skip for now
          continue;
        }

        const { data, error } = await supabase
          .from('phc_referrals')
          .insert({
            consultation_id: linked.remoteId,
            patient_id: r.patientId,
            referred_by: r.referredBy,
            referral_facility: r.referralFacility,
            referral_reason: r.referralReason,
            urgency: r.urgency,
            status: r.status,
          })
          .select('id')
          .single();

        if (error) throw error;
        await phcOfflineDb.markReferralSynced(r.localId, data.id);
        synced++;
      } catch (err) {
        logger.error('[PHC Sync] Failed to sync referral:', err);
        failed++;
      }
    }

    // Sync lab results
    const unsyncedLabs = await phcOfflineDb.getUnsyncedLabResults();
    for (const l of unsyncedLabs) {
      try {
        const { data, error } = await (supabase as any)
          .from('phc_lab_results')
          .insert({
            patient_id: l.patientId,
            phc_site_link_id: l.phcSiteLinkId,
            trial_site_id: l.trialSiteId,
            consultation_id: l.consultationId,
            collected_by: l.collectedBy,
            collected_at: l.collectedAt,
            resulted_at: l.resultedAt,
            sample_type: l.sampleType,
            test_name: l.testName,
            test_code: l.testCode,
            result_value: l.resultValue,
            result_unit: l.resultUnit,
            reference_range: l.referenceRange,
            abnormal_flag: l.abnormalFlag,
            status: l.status,
            external_lab_ref: l.externalLabRef,
            notes: l.notes,
            payload: l.payload ?? {},
            offline_submission_uuid: l.offlineSubmissionUuid,
            synced_at: new Date().toISOString(),
          })
          .select('id')
          .single();
        if (error) throw error;
        await phcOfflineDb.markLabResultSynced(l.localId, data.id);
        synced++;
      } catch (err) {
        logger.error('[PHC Sync] Failed to sync lab result:', err);
        failed++;
      }
    }

    // Sync lab result updates (result entry against existing specimens)
    const unsyncedUpdates = await phcOfflineDb.getUnsyncedLabResultUpdates();
    for (const u of unsyncedUpdates) {
      try {
        const { error } = await (supabase as any)
          .from('phc_lab_results')
          .update(u.patch)
          .eq('id', u.remoteId);
        if (error) throw error;
        await phcOfflineDb.markLabResultUpdateSynced(u.remoteId);
        synced++;
      } catch (err) {
        logger.error('[PHC Sync] Failed to sync lab result update:', err);
        failed++;
      }
    }

    // EBO-ALERTS: drain queued VHF signal reports on the same pass.
    try {
      const vhfResult = await syncVhfSignals();
      synced += vhfResult.synced;
      failed += vhfResult.failed;
    } catch (err) {
      logger.error('[PHC Sync] VHF signal sync error:', err);
    }
  } finally {
    isSyncing = false;
  }

  // Dispatch event for UI updates
  window.dispatchEvent(new CustomEvent('phc-sync-complete', { detail: { synced, failed } }));
  return { synced, failed };
}

// NOTE: The `online` event listener is now handled by the unified syncOrchestrator
// in src/lib/syncOrchestrator.ts to prevent all sync mechanisms from firing simultaneously.
// syncPHCData() is registered as a sync task with priority 3.
